import { useEffect } from 'react'

declare global {
  interface Window {
    /** Tracks which SF variant loaders we've already injected so a page
     *  with multiple badges (TBS + SF + Slashdot) doesn't double-inject
     *  the same script. Shared across StrictMode double-invoke and SPA
     *  re-navigation. */
    __sfBadgesLoaded?: Set<string>
  }
}

interface SourceForgeBadgeProps {
  /** SF's variant identifier: `tbs` (Top Business Software),
   *  `sf` (SourceForge), `sd` (Slashdot), etc. Drives both the script
   *  URL's `variant_id` query param and the div's `data-variant-id`. */
  variantId: 'tbs' | 'sf' | 'sd'
  /** Badge style identifier from SF (e.g. `most-loved`,
   *  `customers-love-us-white`, `users-love-us-new-white`). */
  badge: string
  /** Outbound destination shown to the user when they click the badge
   *  — different domain per variant. */
  href: string
  /** Accessible text shown if the SF script fails or hasn't loaded yet. */
  label?: string
  /** Optional className for outer wrapper (margin/layout adjustments). */
  className?: string
}

/**
 * Generic SourceForge / TBS / Slashdot trust badge.
 *
 * Renders the SF-required `.sf-root` div with the right data-attributes,
 * then injects the loader script (once per variant) which mutates the
 * div into an actual badge image. Pulled into a single parameterised
 * component so the footer can stack TBS + SourceForge + Slashdot side
 * by side without three near-duplicate snippets.
 *
 * Note: the rendered badge is a runtime artifact of SF's loader. The
 * prerendered HTML contains only the empty `.sf-root` container; real
 * visitors see the badge once JS runs.
 */
export function SourceForgeBadge({
  variantId,
  badge,
  href,
  label = 'DocumentIQ Reviews',
  className,
}: SourceForgeBadgeProps) {
  useEffect(() => {
    if (typeof window === 'undefined') return
    const loaded = (window.__sfBadgesLoaded ??= new Set<string>())
    if (loaded.has(variantId)) return
    loaded.add(variantId)

    const sc = document.createElement('script')
    sc.async = true
    sc.src = `https://b.sf-syn.com/badge_js?sf_id=4094876&variant_id=${variantId}`
    document.body.appendChild(sc)
  }, [variantId])

  return (
    <div
      className={`sf-root ${className ?? ''}`.trim()}
      data-id="4094876"
      data-badge={badge}
      data-variant-id={variantId}
      style={{ width: 125 }}
    >
      <a href={href} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    </div>
  )
}
